/**
 * filter-node.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { AbstractControl, FormArray, FormControl, Validators }
    from "@angular/forms";
import { CollectionType, SERVER_TYPES, ServerDataType }
    from "../common/data/data-types";
import { TypedData, TypedDataHolder } from "../common/data/typed-data";
import { AggregationFilterNode }
    from "../server/messages/aggregation/start-aggregator-request";
import { arrayIntersects, arraySubset, setEquals }
    from "../support/utilities/functions";
import {
    BinaryOperatorSignature,
    FilterNodeType,
    Is,
    Operator,
    OperatorName,
    Operators,
    OperatorSignature
} from "./operators";
import { StdVariable } from "./typed-parameter";

const NUMBER_TYPES_BY_NARROWNESS : ServerDataType[] = [
    "whole number",
    "natural number",
    "integer",
    "float"
];

// temporary fill until DataTypes are revamped
function widensTo (type : ServerDataType, widening : ServerDataType) : boolean
{
    if (type === widening) {
        // if the types are the same, they can trivially "widen" to eachother
        return true;
    }
    const narrowness : number = NUMBER_TYPES_BY_NARROWNESS.indexOf(type);
    if (narrowness > -1)
    {
        // if it's a number type, only allow widening to later types in the
        // number widening list
        return (narrowness < NUMBER_TYPES_BY_NARROWNESS.indexOf(widening));
    }
    // otherwise there are no allowed widenings of server types
    // (json->string is a client type widening)
    return false;
}

function withSubs (types : Set<ServerDataType>) : Set<ServerDataType>
{
    if (!types)
    {
        return types;
    }
    types.forEach((type : ServerDataType) => {
        let narrowness : number = NUMBER_TYPES_BY_NARROWNESS.indexOf(type);
        for (narrowness; narrowness > -1; narrowness--)
        {
            types.add(NUMBER_TYPES_BY_NARROWNESS[narrowness]);
        }
    });
    return types;
}

function withSupers (types : Set<ServerDataType>) : Set<ServerDataType>
{
    if (!types)
    {
        return types;
    }
    types.forEach((type : ServerDataType) => {
        const narrowness : number = NUMBER_TYPES_BY_NARROWNESS.indexOf(type);
        if (narrowness > -1)
        {
            NUMBER_TYPES_BY_NARROWNESS.slice(narrowness + 1).forEach(
                (wider : ServerDataType) => types.add(wider));
        }
    });
    return types;
}

export abstract class FilterNode
{
    public abstract nodeType : FilterNodeType;
    public parent : OperatorNode | null = null;
    public idWithinParent : number;
    // public typeControl? : FormControl;
    public depth : number = 0;
    public abstract marshal () : AggregationFilterNode;
    public abstract returnTypes () : Set<ServerDataType>;
    public abstract disable () : void;
    public parentAccepts : Set<ServerDataType> = new Set();
    public abstract parentExpectationChange (
        expected : Set<ServerDataType>,
        cardinality? : number
    ) : Set<ServerDataType>;
    public allowedVariables : TypedDataHolder[] = [];

    public revert (host? : {filter : FilterNode}) : void
    {
        if (this.parent)
        {
            this.parent.addChild(this.idWithinParent, new UndefinedNode());
        }
        else if (host)
        {
            throw new Error("Unsupported!");
            // const node : FilterNode = new UndefinedNode();
            // node.allowedVariables = this.allowedVariables;
        }
    }
    public abstract toReadable () : string;
    public abstract applySchemaFilter (
        filter : AggregationFilterNode,
        variableList : TypedDataHolder[]
    ) : void;
}

export class ConstantNode extends FilterNode
{
    public nodeType : FilterNodeType = "value";
    public readonly name : string = "filter node";
    public cardinality : number = 1;
    public collectionType : CollectionType = "none";
    public type : ServerDataType = null;
    public allowedTypes : Set<ServerDataType> = new Set(SERVER_TYPES);
    public value : any;
    public valueControl : AbstractControl;
    public typeControl : FormControl;
    public restrictions : any[] = null;
    public constructor ()
    {
        super();
        this.createValueControl(false);
        this.typeControl = new FormControl(null, Validators.required);
        this.typeControl.valueChanges.subscribe(
            (newType : any) => {
                this.value = null;
                this.type = newType;
                this.parent.childReturnChange(this, this.idWithinParent);
            });
    }
    private createValueControl (asArray : boolean)
    {
        if (asArray)
        {
            this.valueControl = new FormArray([], Validators.required);
        }
        else
        {
            this.valueControl = new FormControl(null, Validators.required);
        }
        this.valueControl.valueChanges.subscribe(
            (val : any) => this.value = val);
    }
    public returnTypes () : Set<ServerDataType>
    {
        if (this.type)
        {
            return new Set([this.type]);
        }
        else
        {
            return this.allowedTypes;
        }
    }
    public marshal () : AggregationFilterNode
    {
        if (this.typeControl.invalid || this.valueControl.invalid)
        {
            throw new Error("Missing value in constant node");
        }
        return {
            nodeType: "constant",
            cardinality: this.cardinality,
            collectionType: this.cardinality === 0 ? "list" : "none",
            type: this.type,
            value: TypedData.serialize(Object.assign({}, this))
        };
    }
    public toReadable () : string
    {
        return `<em>${TypedData.toString(this, this.value)}</em>`;
    }
    public parentExpectationChange (
        expected : Set<ServerDataType>,
        cardinality? : number
    )
        : Set<ServerDataType>
    {
        this.parentAccepts = expected;
        this.allowedTypes = new Set(SERVER_TYPES.filter(
            (t : ServerDataType) => this.parentAccepts.has(t)));
        if (!this.parentAccepts.has(this.type))
        {
            this.valueControl.reset();
            if (this.parentAccepts.size === 1)
            {
                this.type = this.parentAccepts.values().next().value;
                this.typeControl.setValue(this.type, {emitEvent: false});
            }
            else
            {
                this.type = null;
                this.typeControl.reset(null, {emitEvent: false});
            }
        }
        if (this.parent && this.parent.childValueRestrictions)
        {
            this.restrictions = this.parent.childValueRestrictions;
        }
        else
        {
            this.restrictions = [];
        }

        if (cardinality !== this.cardinality)
        {
            this.cardinality = Number.isInteger(cardinality) ? cardinality : 1;
            this.createValueControl(cardinality !== 1);
        }
        if (this.allowedTypes.size === 1)
        {
            this.typeControl.disable();
        }
        else
        {
            this.typeControl.enable();
        }
        return this.returnTypes();
    }
    public disable () : void
    {
        this.valueControl.disable();
        this.typeControl.disable({emitEvent: false});
    }

    public static fromSchema (
        filter : AggregationFilterNode,
        variableList : TypedDataHolder[]
    ) : ConstantNode
    {
        const n : ConstantNode = new ConstantNode();
        n.cardinality = filter.cardinality;
        n.collectionType = filter.collectionType;
        return n;
    }

    public applySchemaFilter (filter : AggregationFilterNode) : void
    {
        this.type = filter.type;
        this.typeControl.setValue(this.type, {emitEvent: false});
        this.valueControl.setValue(
            TypedData.deserialize(filter as TypedDataHolder));
    }
}

export class VariableNode extends FilterNode
{
    public nodeType : FilterNodeType = "variable";
    public variable : TypedDataHolder;
    public filteredVariables : TypedDataHolder[] = [];
    public variableControl? : FormControl;
    public constructor ()
    {
        super();
        this.variableControl = new FormControl(null, Validators.required);
        this.variableControl.valueChanges.subscribe((varName : string) => {
            if (varName)
            {
                this.variable = this.allowedVariables.find(
                    (varObj : TypedDataHolder) => varObj.name === varName);
                if (!this.variable)
                {
                    throw new Error(`Could not find variable ${varName}`);
                }
                if (this.variable.restrictions)
                {
                    this.parent.childValueRestrictions =
                        this.variable.restrictions;
                }
            }
            else
            {
                this.variable = null;
                this.parent.childValueRestrictions = null;
            }
            this.parent.childReturnChange(this, this.idWithinParent);
        });
    }
    public returnTypes () : Set<ServerDataType> | null
    {
        if (!this.variable)
        {
            return new Set(this.filteredVariables.map(
                (td : TypedDataHolder) => td.type as ServerDataType));
        }
        else
        {
            return new Set([this.variable.type as ServerDataType]);
        }
    }
    public parentExpectationChange (
        expected : Set<ServerDataType>,
        cardinality? : number
    ) : Set<ServerDataType>
    {
        this.parentAccepts = expected;
        this.filteredVariables = this.allowedVariables.filter(
            (td : TypedDataHolder) =>
                this.parentAccepts.has(td.type as ServerDataType)
                    && (!Number.isInteger(cardinality)
                        || td.cardinality === cardinality));
        if (this.variable
            && this.filteredVariables.indexOf(this.variable) === -1)
        {
            if (this.filteredVariables.length === 1)
            {
                this.variableControl.setValue(this.filteredVariables[0]);
            }
            else
            {
                this.variableControl.reset();
            }
        }
        return this.returnTypes();
    }
    public marshal () : AggregationFilterNode
    {
        if (this.variableControl.invalid)
        {
            throw new Error("Missing value in variable node");
        }
        if (!this.variable)
        {
            console.error(this);
            throw new Error("Undefined variable in node");
        }
        return {
            nodeType: "variable",
            variable: this.variable.name
        };
    }
    public toReadable () : string
    {
        if (this.variable)
        {
            return `<kbd>${this.variable.name}</kbd>`;
        }
        else
        {
            return `<kbd>(UNSET)</kbd>`;
        }
    }
    public disable () : void
    {
        this.variableControl.disable({emitEvent: false});
    }

    public static fromSchema () : VariableNode
    {
        return new VariableNode();
    }

    public applySchemaFilter (
        filter : AggregationFilterNode,
        variableList : TypedDataHolder[]
    ) : void
    {
        this.variableControl.setValue(filter.variable);
    }
}

export class UndefinedNode extends FilterNode
{
    public nodeType : FilterNodeType = null;
    public typeControl : FormControl = new FormControl(null,
        Validators.required);
    public allowsOperators : boolean = true;
    constructor ()
    {
        super();
        this.typeControl.valueChanges.subscribe((val : FilterNodeType) => {
            if (val)
            {
                this.changeType(val);
            }
        });
    }
    public marshal () : AggregationFilterNode
    {
        throw new Error("Cannot marshal undefined filter node!");
    }
    public toReadable () : string
    {
        return "???";
    }
    public returnTypes () : null
    {
        return null;
    }
        public disable () : void
    {
        this.typeControl.disable();
    }
    public parentExpectationChange (expected : Set<ServerDataType>)
        : Set<ServerDataType>
    {
        this.parentAccepts = expected;
        this.allowsOperators = (
            !this.parent || !this.parent.childValueRestrictions);
        return null;
    }
    protected changeType (newType : FilterNodeType) : void
    {
        let node : FilterNode;
        switch (newType)
        {
            case "variable":
                node = new VariableNode();
                break;
            case "value":
                node = new ConstantNode();
                break;
            case "expression":
                node = new OperatorNode(Operators.get("is"));
                break;
            default:
                throw new Error("Disallowed node type!");
        }
        this.parent.addChild(this.idWithinParent, node);
    }
    public applySchemaFilter () : never
    {
        throw new Error(`Can't apply schema to undefined node!`);
    }
}

type TypeSet = Set<ServerDataType> | null;
interface Triad<T> {
    self : T;
    left : T;
    right : T;
}
type TypeTriad = Triad<TypeSet>;
type PartialSignatures = Triad<OperatorSignature[]>;

/**
 * ...
 */
export class OperatorNode extends FilterNode
{
    public nodeType : FilterNodeType = "expression";
    public operator : Operator;
    public operands : FilterNode[] = [];
    public operatorControl? : FormControl;
    public availableOperators : Operator[] = Operators.All;

    public expects : TypeTriad = {
        self: null,
        left: null,
        right: null
    };
    public returns : TypeTriad = {
        self: null,
        left: null,
        right: null
    };
    public signatures : PartialSignatures = {
        self: [],
        left: [],
        right: []
    };

    private _childValueRestrictions : any[] = null;
    public get childValueRestrictions () : any[]
    {
        return this._childValueRestrictions;
    }
    public set childValueRestrictions (restrictions : any[])
    {
        this._childValueRestrictions = restrictions;
        if (this.right instanceof ConstantNode)
        {
            this.right.restrictions = this._childValueRestrictions;
        }
        else if (this.right && restrictions !== null)
        {
            this.addChild(1, new ConstantNode());
        }
    }

    public cardinalities : Triad<number | null> =
        {left: null, right: null, self: null};

    public static readonly DefaultOp : Operator = Operators.get("is");
    public static readonly RootExpectations : TypeSet =
        new Set(["boolean"]) as TypeSet;

    public constructor (
        op : Operator = OperatorNode.DefaultOp,
        operands? : FilterNode[],
        public root : boolean = false,
        public allowedVariables : TypedDataHolder[] = []
    )
    {
        super();
        this.setOperator(op);

        if (root)
        {
            this.parentExpectationChange(OperatorNode.RootExpectations);
        }

        this.operatorControl = new FormControl(this.operator.name,
            Validators.required);
        this.operatorControl.valueChanges.subscribe((opName : string) => {
            this.setOperator(Operators.get(opName));
            // if (this.parent)
            // {
            //     this.parent.childReturnChange(this, this.idWithinParent);
            // }
        });

        if (operands)
        {
            operands.forEach((o : FilterNode, i : number) => {
                this.addChild(i, o);
            });
        }
    }

    public addChild (i : number, node : FilterNode) : void
    {
        const prevNode : FilterNode = this.operands[i];
        if (prevNode)
        {
            prevNode.parent = null;
        }

        this.operands[i] = node;
        node.parent = this;
        node.idWithinParent = i;
        node.allowedVariables = this.allowedVariables;
        this.depth = node.depth + 1;

        if (i === 0)
        {
            this.determineLeftExpectations(true);
        }
        else
        {
            this.determineRightExpectations(true);
        }
    }

    private determineOperators () : boolean
    {
        if (this.expects.self)
        {
            if (!this.expects.self.size)
            {
                this.availableOperators = [];
                console.error(this);
                throw new Error("Operator has empty expectations!");
            }
            this.availableOperators = Operators.All.filter(
                (op : Operator) => (
                    op.signature.some(
                        (sig : OperatorSignature) =>
                            this.expects.self.has(sig.returns))
                    && (op.accepts(this))));
        }
        else
        {
            this.availableOperators = Array.from(Operators.All);
        }

        if (this.availableOperators.indexOf(this.operator) === -1)
        {
            return this.setOperator();
        }
        return false;
    }

    private setOperator (op? : Operator) : boolean
    {
        if (!op)
        {
            op = OperatorNode.DefaultOp;
        }
        if (this.operator !== op)
        {
            this.operator = op;
            this.constrainByOperator();
            return true;
        }
    }

    private constrainByOperator () : void
    {
        if (this.expects.self)
        {
            this.signatures.self = this.operator.signature.filter(
                (sig : OperatorSignature) =>
                    this.expects.self.has(sig.returns));
        }
        else
        {
            this.signatures.self = this.operator.signature.map((x : any) => x);
        }

        this.determineLeftExpectations();
    }

    private determineLeftExpectations (force : boolean = false) : void
    {
        if (!this.left)
        {
            return this.addChild(0, new UndefinedNode());
        }
        const oldExpects : TypeSet = this.expects.left;
        this.expects.left = new Set(
            this.signatures.self.map((sig : OperatorSignature) => sig.left));
        const oldCard : number | null = this.cardinalities.left;
        this.cardinalities.left =
            this.operator.collectionArguments.left ? 0 : 1;
        const changed : boolean =
            !setEquals(oldExpects, this.expects.left)
            || oldCard !== this.cardinalities.left;
        if (force || changed)
        {
            this.returns.left =
                this.left.parentExpectationChange(
                    this.expects.left, this.cardinalities.left);
            if (this.returns.left && this.returns.left.size === 0)
            {
                return this.addChild(0, new UndefinedNode());
            }
        }
        this.constrainByLeft();
    }

    private constrainByLeft () : void
    {
        if (this.returns.left)
        {
            this.signatures.left = this.signatures.self.filter(
                (sig : OperatorSignature) => this.returns.left.has(sig.left));
        }
        else
        {
            this.signatures.left = this.signatures.self.map((x : any) => x);
        }

        this.determineRightExpectations();
    }

    private determineRightExpectations (force : boolean = false) : void
    {
        if (!this.operator.isUnary())
        {
            if (this.operator.isBinary() && !this.right)
            {
                return this.addChild(1, new UndefinedNode());
            }
            if (this.right)
            {
                const oldExpects : TypeSet = this.expects.right;
                this.expects.right = new Set(
                    this.signatures.left.map(
                        (sig : BinaryOperatorSignature) => sig.right));
                const oldCard : number | null = this.cardinalities.right;
                this.cardinalities.right =
                    this.operator.collectionArguments.right ? 0 : 1;
                const changed : boolean =
                    !setEquals(oldExpects, this.expects.right)
                    || oldCard !== this.cardinalities.right;
                if (force || changed)
                {
                    this.returns.right =
                        this.right.parentExpectationChange(
                            this.expects.right, this.cardinalities.right);
                    if (this.returns.right && this.returns.right.size === 0)
                    {
                        return this.addChild(1, new UndefinedNode());
                    }
                }
            }
        }
        else
        {
            while (this.right)
            {
                const removed : FilterNode = this.operands.pop();
                removed.parent = null;
                removed.idWithinParent = null;
            }
            this.expects.right = null;
            this.cardinalities.right = null;
        }
        this.constrainByRight();
    }

    private constrainByRight () : void
    {
        if (this.returns.right)
        {
            this.signatures.right = this.signatures.left.filter(
                (sig : BinaryOperatorSignature) =>
                    this.returns.right.has(sig.right));
        }
        else
        {
            this.signatures.right = this.signatures.left.map((x : any) => x);
        }
        this.determineReturns();
    }

    private determineReturns () : void
    {
        this.returns.self = new Set(
            this.signatures.right.map(
                (sig : OperatorSignature) => sig.returns));
    }

    /**
     * Indicate a parent has new expectations for this node's return type(s)
     * @param expectations The new return type options
     */
    public parentExpectationChange (
        expects : Set<ServerDataType>,
        cardinality? : number
    ) : Set<ServerDataType>
    {
        if (!setEquals(expects, this.expects.self)
            || cardinality !== this.cardinalities.self)
        {
            this.expects.self = expects;
            this.cardinalities.self = cardinality;
            if (!this.determineOperators())
            {
                this.constrainByOperator();
            }
        }
        return this.returns.self;
    }

    public childReturnChange (child : FilterNode, childIndex : number) : void
    {
        if (childIndex === 0)
        {
            this.returns.left = this.left.returnTypes();
            this.constrainByLeft();
        }
        else
        {
            this.returns.right = this.right.returnTypes();
            this.constrainByRight();
        }
    }

    public get left () : FilterNode
    {
        return this.operands[0];
    }
    public get right () : FilterNode
    {
        if (this.operands.length !== 2)
        {
            return null;
        }
        else
        {
            return this.operands[1];
        }
    }

    public returnTypes () : Set<ServerDataType>
    {
        return this.returns.self;
    }

    public marshal () : AggregationFilterNode
    {
        if (this.operatorControl.invalid)
        {
            throw new Error("Missing value in operator node");
        }
        if (this.operator.name === "is")
        {
            // the IS operator is client-only and flattens to its single operand
            return this.left.marshal();
        }
        if (this.operator.name === "all of")
        {
            if (this.operands.length === 1)
            {
                return this.left.marshal();
            }
            else if (this.operands.length === 2)
            {
                return ({
                    nodeType: "operator",
                    operator: Operators.get("and").protocolName,
                    operands: this.operands.map(
                        (fn : FilterNode) => fn.marshal())
                });
            }
            else
            {
                const andOp : string = Operators.get("and").protocolName;
                const args : AggregationFilterNode[] =
                    this.operands.map((o : FilterNode) => o.marshal());
                while (args.length > 1)
                {
                    for (let i : number = 0; i < args.length - 1; i ++)
                    {
                        args.splice(i, 2, {
                            nodeType: "operator",
                            operator: andOp,
                            operands: [args[i], args[i + 1]]
                        });
                    }
                }
                return args[0];
                // const root : AggregationFilterNode = {
                //     nodeType: "operator",
                //     operator: andOp,
                //     operands: []
                // };
                // let n : AggregationFilterNode = root;
                // for (let i : number = 0; i++; i < this.operands.length - 2)
                // {
                //     n.operands.push(this.operands[i].marshal());
                //     n.operands.push({
                //         nodeType: "operator",
                //         operator: andOp,
                //         operands: []
                //     });
                //     n = n.operands[1];
                // }
                // n.operands.push(
                //     this.operands[this.operands.length - 2].marshal());
                // n.operands.push(
                //     this.operands[this.operands.length - 1].marshal());
                // return root;
            }
        }
        else
        {
            return ({
                nodeType: "operator",
                operator: this.operator.protocolName,
                operands: this.operands.map((fn : FilterNode) => fn.marshal())
            });
        }
    }

    public toReadable () : string
    {
        if (this.operator.isBinary())
        {
            return `(${this.operator.label(this.left, [this.right])})`;
        }
        else if (this.operator.isVariableArity())
        {
            return`(${this.operator.label(this.left, this.operands.slice(1))})`;
        }
        else
        {
            return `(<em>${this.operator.label(this.left)}</em>)`;
        }
    }

    public disable () : void
    {
        this.operatorControl.disable({emitEvent: false});
        this.operands.forEach((node : FilterNode) => node.disable());
		}

		private static isValidSimpleClause (filter : AggregationFilterNode)
			: boolean
		{
			return (filter.nodeType === "operator"
					&& filter.operands
					&& filter.operands[1]
					&& filter.operands[1].nodeType === "constant"
					&& filter.operands[0]
					&& (filter.operands[0].nodeType === "variable"
							|| filter.operands[0].nodeType === "argument"));
		}
		
		public static simpleFromSchema (
			filter : AggregationFilterNode,
			variableList : TypedDataHolder[],
			isRoot : boolean = false,
			contextVariable? : StdVariable) : OperatorNode
		{
			let clauses : OperatorNode[] = [];
			if (OperatorNode.isValidSimpleClause(filter))
			{
				clauses = [OperatorNode.fromSchema(
					filter, variableList, false, contextVariable)];
			}
			else
			{
				const and = Operators.get("and").protocolName;

				const identity = (_: any) => _;
				let toProcess : AggregationFilterNode[] = [filter];
				while (toProcess.length > 0)
				{
					const current = toProcess.shift();
					if (current.nodeType !== "operator")
					{
						console.error(current);
						throw new Error("Found nonoperator node in supposed simple filter");
					}
					if (current.operator === and)
					{
						toProcess.splice(0, 0, ...current.operands);
					}
					else
					{
						if (OperatorNode.isValidSimpleClause(current))
						{
							clauses.push(OperatorNode.fromSchema(
								current,
								variableList,
								false,
								contextVariable));
						}
						else
						{
							console.error(current);
							throw new Error("Found an invalid leaf clause for simple filter");
						}
					}
				}
			}
			return new OperatorNode(
				Operators.get("all of"), clauses, true, variableList);
		}

    public static fromSchema (
        filter : AggregationFilterNode,
        variableList : TypedDataHolder[],
				isRoot : boolean = false,
				contextVariable? : StdVariable,
				attemptSimpleSynthesis : boolean = false
    ) : OperatorNode
    {
				if (attemptSimpleSynthesis)
				{
					try
					{
						return OperatorNode.simpleFromSchema(
							filter, variableList, isRoot, contextVariable);
					}
					catch (err)
					{
						console.log("Attempted to parse as simple filter, received error:")
						console.warn(err);
					}
				}

        const children : FilterNode[] =  filter.operands.map(
            (child : AggregationFilterNode) => {
                switch (child.nodeType)
                {
                    case "operator":
                        return OperatorNode.fromSchema(
													child, variableList, false, contextVariable);
                    case "variable":
                        return VariableNode.fromSchema();
                    case "constant":
												return ConstantNode.fromSchema(child, variableList);
										case "argument":
												return ArgumentNode.fromSchema(contextVariable);
                    default:
                        throw new Error(
                            `Cannot demarshal unknown filter node types`);
                }
            });
        // console.info(isRoot, filter.operator, Operators.getByProtocol(filter.operator));
        // console.info(Operators.All);
        const n : OperatorNode = new OperatorNode(
            Operators.getByProtocol(filter.operator),
            children,
            isRoot,
            variableList
        );
        children.forEach((c : FilterNode, i : number) =>
            c.applySchemaFilter(filter.operands[i], variableList));
        return n;
    }

    public applySchemaFilter () : void
    {
        // do nothing
    }

    public static BasicEquality (vars : TypedDataHolder[], root : boolean)
        : OperatorNode
    {
        const eqNode : OperatorNode = new OperatorNode(
            Operators.get("equals"),
            [],
            root,
            vars
        );
        eqNode.addChild(0, new VariableNode());
        eqNode.addChild(1, new ConstantNode());
        return eqNode;
    }
}

export class ArgumentNode extends FilterNode
{
	public nodeType : FilterNodeType = "argument";
	constructor (protected variable : StdVariable)
	{
		super()
	}
	public marshal () : AggregationFilterNode
	{
			return {nodeType: "argument"};
	}
	public toReadable () : string
	{
			return `<kbd>this entry of ${this.variable.displayName}</kbd>`;
	}
	public returnTypes () : Set<ServerDataType>
	{
			return new Set([this.variable.type]);
	}
	public parentExpectationChange (
		expected : Set<ServerDataType>,
		cardinality? : number
	) : Set<ServerDataType>
	{
		if (this.variable.restrictions)
		{
			this.parent.childValueRestrictions =
					this.variable.restrictions;
		}
		return this.returnTypes();
	}
	public disable ()
	{
		/* do nothing */
	}
	public applySchemaFilter ()
	{
		/* do nothing */
	}
	public static fromSchema (variable : StdVariable) : ArgumentNode
	{
		return new ArgumentNode(variable);
	}
}


// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/filter-node.ts